import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { STORE_CURRENCY, contentFor, formatPrice, storePath } from "@nh/shared";
import { Alert } from "@/shared/ui/core";
import { QuantityStepper } from "@/features/cart";
import { ProductThumb } from "@/features/admin/products/components/ProductThumb";
import { usePublicStore } from "./store.api";
import { StoreCheckoutModal } from "./StoreCheckoutModal";
import { StoreLayout } from "./StoreLayout";

/**
 * La ficha de un producto DENTRO de una tienda personalizada — la
 * "versión del producto del Annieshop, con su costo" (nota 3 de Anahí):
 * el precio es el de la tienda, el stock es el del franquiciatario, y
 * comprar sale por el checkout con la tienda a cuestas.
 */
export function StoreProductPage() {
  const { t } = useTranslation();
  const { slug, productSlug } = useParams<{ slug: string; productSlug: string }>();
  const store = usePublicStore(slug);
  const [quantity, setQuantity] = useState(1);
  const [comprando, setComprando] = useState(false);

  if (store.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ds-cream">
        <p className="text-[14px] text-ds-ink-3">{t("Abriendo la tienda…")}</p>
      </div>
    );
  }

  if (store.isError || !store.data) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Alert variant="error" title={t("Esa tienda no existe")}>
          <Link to="/shop" className="underline underline-offset-2">
            {t("Ir a la tienda de It's The Best")}
          </Link>
        </Alert>
      </div>
    );
  }

  const { store: info, items } = store.data;
  const entry = items.find((item) => item.product.slug === productSlug);

  if (!entry || entry.stock === 0) {
    return (
      <StoreLayout store={info}>
        <div className="mx-auto max-w-md px-4 py-16">
          <Alert variant="info" title={t("Ese producto no está en esta tienda")}>
            <Link to={storePath(info.slug)} className="underline underline-offset-2">
              {t("Volver a {{name}}", { name: info.name })}
            </Link>
          </Alert>
        </div>
      </StoreLayout>
    );
  }

  const { product, stock } = entry;
  // El COLOR de la tienda pinta también la ficha (Anahí, 27-ago):
  // precio, botón de comprar y acentos — la config de Mi tienda manda.
  const accent = info.branding.brandColor;
  const content = contentFor(product.translations, "es");
  // El precio YA resuelto por el servidor: el propio de la tienda o el
  // de socio. La ficha lo enseña; el cobro lo vuelve a calcular.
  const price = entry.priceCents;
  const publicPrice = product.prices[STORE_CURRENCY];
  const cheaper =
    typeof price === "number" && typeof publicPrice === "number" && price < publicPrice;

  /*
   * SIN redirección al checkout de la casa (Anahí, 27-ago): el pedido
   * se arma en el MODAL, dentro de la tienda, y se paga directo con
   * quien vende.
   */
  function comprar() {
    setComprando(true);
  }

  return (
    <StoreLayout store={info}>
      <div className="mx-auto w-full max-w-[900px] px-4 py-8 sm:px-8">
        <Link
          to={storePath(info.slug)}
          className="inline-flex items-center gap-2 text-[13px] font-medium underline-offset-2 hover:underline"
          style={{ color: accent }}
        >
          <ArrowLeft className="h-4 w-4" />
          {t("Volver a {{name}}", { name: info.name })}
        </Link>

        <div className="mt-4 grid gap-6 rounded-3xl border border-ds-border bg-white p-6 shadow-ds-float sm:grid-cols-[240px_1fr] sm:p-8">
          <ProductThumb url={product.imageUrl} name={content.name} size="lg" />

          <div className="grid content-start gap-3">
            <h1 className="text-[24px] font-semibold leading-tight text-ds-ink">
              {content.name}
            </h1>
            {content.tagline && <p className="text-[14px] text-ds-ink-3">{content.tagline}</p>}

            <p className="text-[26px] font-semibold" style={{ color: accent }}>
              {typeof price === "number" ? formatPrice(price, STORE_CURRENCY) : "—"}
              {cheaper && (
                <span className="ml-2 align-middle text-[14px] font-normal text-ds-ink-3 line-through">
                  {formatPrice(publicPrice as number, STORE_CURRENCY)}
                </span>
              )}
            </p>
            <p className="text-[12px] text-ds-ink-3">
              {t("Precio de {{name}} · {{n}} disponibles", { name: info.name, n: stock })}
            </p>

            {content.description && (
              <p className="text-[14px] leading-relaxed text-ds-ink-3">
                {content.description}
              </p>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-3">
              <QuantityStepper
                value={quantity}
                onChange={setQuantity}
                max={stock}
                label={content.name}
              />
              <button
                type="button"
                disabled={typeof price !== "number"}
                onClick={comprar}
                className="inline-flex h-12 items-center gap-2 rounded-full px-7 text-[14px] font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                style={{ background: accent }}
              >
                <ShoppingBag className="h-4 w-4" />
                {t("Comprar")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <StoreCheckoutModal
        open={comprando}
        onClose={() => setComprando(false)}
        store={info}
        item={entry}
        quantity={quantity}
        accent={accent}
      />
    </StoreLayout>
  );
}
